import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  Download, RefreshCw, LogIn, LogOut, Plus, Pencil, Trash2,
  FileUp, FileDown, RotateCcw, Clock, Filter, ChevronDown,
  ChevronRight, Eye, Sparkles
} from 'lucide-react';

// ─── Config actions ──────────────────────────────────────────
const actionConfig = {
  LOGIN: { label: 'Connexion', verb: 's\'est connecte', icon: LogIn, color: 'text-green-700 bg-green-50 border-green-200', pill: 'bg-green-100 text-green-700 border-green-200' },
  LOGOUT: { label: 'Deconnexion', verb: 's\'est deconnecte', icon: LogOut, color: 'text-gray-600 bg-gray-50 border-gray-200', pill: 'bg-gray-100 text-gray-600 border-gray-200' },
  CREATE: { label: 'Creation', verb: 'a cree', icon: Plus, color: 'text-blue-700 bg-blue-50 border-blue-200', pill: 'bg-blue-100 text-blue-700 border-blue-200' },
  UPDATE: { label: 'Modification', verb: 'a modifie', icon: Pencil, color: 'text-amber-700 bg-amber-50 border-amber-200', pill: 'bg-amber-100 text-amber-700 border-amber-200' },
  DELETE: { label: 'Suppression', verb: 'a supprime', icon: Trash2, color: 'text-red-700 bg-red-50 border-red-200', pill: 'bg-red-100 text-red-700 border-red-200' },
  EXPORT: { label: 'Export', verb: 'a exporte', icon: FileDown, color: 'text-purple-700 bg-purple-50 border-purple-200', pill: 'bg-purple-100 text-purple-700 border-purple-200' },
  IMPORT: { label: 'Import', verb: 'a importe', icon: FileUp, color: 'text-indigo-700 bg-indigo-50 border-indigo-200', pill: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  RESTORE_BACKUP: { label: 'Restauration', verb: 'a restaure une sauvegarde', icon: RotateCcw, color: 'text-orange-700 bg-orange-50 border-orange-200', pill: 'bg-orange-100 text-orange-700 border-orange-200' },
  microsoft_oauth_callback: { label: 'Connexion Microsoft', verb: 's\'est connecte via Microsoft', icon: LogIn, color: 'text-blue-700 bg-blue-50 border-blue-200', pill: 'bg-blue-100 text-blue-700 border-blue-200' },
  access_request_approved: { label: 'Approbation', verb: 'a approuve un acces', icon: Plus, color: 'text-green-700 bg-green-50 border-green-200', pill: 'bg-green-100 text-green-700 border-green-200' },
  access_request_rejected: { label: 'Refus', verb: 'a refuse un acces', icon: Trash2, color: 'text-red-700 bg-red-50 border-red-200', pill: 'bg-red-100 text-red-700 border-red-200' },
};

const entityLabels = {
  vehicule: 'vehicule', chauffeur: 'chauffeur', operation: 'operation',
  proprietaire: 'partenaire', societe: 'client', user: 'utilisateur',
  incident: 'incident', alert: 'alerte', backup: 'sauvegarde', setting: 'parametre',
};

// ─── Avatar couleurs par utilisateur ─────────────────────────
const avatarColors = [
  'bg-emerald-500', 'bg-blue-500', 'bg-purple-500', 'bg-amber-500',
  'bg-pink-500', 'bg-cyan-500', 'bg-rose-500', 'bg-teal-500',
];
const getAvatarColor = (email) => {
  if (!email) return avatarColors[0];
  const hash = email.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return avatarColors[hash % avatarColors.length];
};
const getInitials = (name, email) => {
  if (name && name.length > 1) {
    const parts = name.trim().split(' ');
    return parts.length > 1 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
  }
  return email ? email.substring(0, 2).toUpperCase() : '??';
};

// ─── Phrase humaine ──────────────────────────────────────────
const buildSentence = (log) => {
  const who = log.user_name || log.user_email?.split('@')[0] || 'Quelqu\'un';
  const cfg = actionConfig[log.action] || {};
  const entity = entityLabels[log.entity_type] || log.entity_type || '';
  const verb = cfg.verb || log.action?.toLowerCase() || 'a effectue une action';

  if (['LOGIN', 'LOGOUT', 'microsoft_oauth_callback'].includes(log.action)) return `${who} ${verb}`;
  if (['RESTORE_BACKUP', 'access_request_approved', 'access_request_rejected'].includes(log.action)) return `${who} ${verb}`;
  if (entity) return `${who} ${verb} un(e) ${entity}`;
  return `${who} ${verb}`;
};

// ─── Résumé narratif du jour ─────────────────────────────────
const buildNarrative = (summary) => {
  if (!summary || !summary.grouped_actions?.length) return null;
  const parts = [];
  const actionCounts = {};
  summary.actions?.forEach((a) => { actionCounts[a._id] = a.count; });

  if (actionCounts.LOGIN) parts.push(`${actionCounts.LOGIN} connexion${actionCounts.LOGIN > 1 ? 's' : ''}`);
  if (actionCounts.CREATE) parts.push(`${actionCounts.CREATE} creation${actionCounts.CREATE > 1 ? 's' : ''}`);
  if (actionCounts.UPDATE) parts.push(`${actionCounts.UPDATE} modification${actionCounts.UPDATE > 1 ? 's' : ''}`);
  if (actionCounts.DELETE) parts.push(`${actionCounts.DELETE} suppression${actionCounts.DELETE > 1 ? 's' : ''}`);
  if (actionCounts.EXPORT) parts.push(`${actionCounts.EXPORT} export${actionCounts.EXPORT > 1 ? 's' : ''}`);
  if (actionCounts.IMPORT) parts.push(`${actionCounts.IMPORT} import${actionCounts.IMPORT > 1 ? 's' : ''}`);

  const userCount = summary.users?.length || 0;
  const userNames = summary.users?.slice(0, 3).map((u) => u.user_name || u._id?.split('@')[0]).join(', ');

  let text = `Aujourd'hui : ${parts.join(', ')}`;
  if (userCount > 0) text += ` par ${userCount} utilisateur${userCount > 1 ? 's' : ''} (${userNames}${userCount > 3 ? '...' : ''})`;
  return text;
};

// ─── Grouper les actions répétitives ─────────────────────────
const groupRepeatedActions = (logs) => {
  const groups = [];
  let i = 0;
  while (i < logs.length) {
    const current = logs[i];
    const key = `${current.user_email}-${current.action}-${current.entity_type || ''}`;
    let count = 1;
    let lastTimestamp = current.timestamp;
    let j = i + 1;
    while (j < logs.length) {
      const next = logs[j];
      const nextKey = `${next.user_email}-${next.action}-${next.entity_type || ''}`;
      if (nextKey === key) {
        count++;
        j++;
      } else {
        break;
      }
    }
    groups.push({ ...current, _count: count, _firstTimestamp: logs[j - 1]?.timestamp || lastTimestamp });
    i = j;
  }
  return groups;
};

// ─── Grouper par jour ────────────────────────────────────────
const groupByDay = (logs) => {
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86400000).toDateString();
  const groups = {};
  logs.forEach((log) => {
    const d = new Date(log.timestamp);
    const key = d.toDateString();
    const label = key === today ? "Aujourd'hui" : key === yesterday ? 'Hier' : d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    if (!groups[label]) groups[label] = [];
    groups[label].push(log);
  });
  return groups;
};

const formatTime = (ts) => new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

// ─── Quick filter pills ──────────────────────────────────────
const quickFilters = [
  { key: '', label: 'Tout', icon: Eye },
  { key: 'LOGIN', label: 'Connexions', icon: LogIn },
  { key: 'UPDATE', label: 'Modifications', icon: Pencil },
  { key: 'CREATE', label: 'Creations', icon: Plus },
  { key: 'DELETE', label: 'Suppressions', icon: Trash2 },
  { key: 'EXPORT,IMPORT', label: 'Import/Export', icon: FileDown },
];

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [expandedLog, setExpandedLog] = useState(null);
  const [activeQuickFilter, setActiveQuickFilter] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({ action: '', entityType: '', startDate: '', endDate: '' });

  useEffect(() => { fetchAll(); }, [page, filters]);

  const fetchAll = () => {
    fetchLogs();
    fetchStats();
    fetchSummary();
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page.toString(), limit: '50' });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const response = await api.get(`/audit-logs?${params}`);
      setLogs(response.data);
    } catch { toast.error('Erreur lors du chargement'); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try { const r = await api.get('/audit-logs/stats'); setStats(r.data); } catch {}
  };

  const fetchSummary = async () => {
    try { const r = await api.get('/audit-logs/daily-summary'); setSummary(r.data); } catch {}
  };

  const handleQuickFilter = (key) => {
    setActiveQuickFilter(key);
    setFilters((prev) => ({ ...prev, action: key }));
    setPage(1);
  };

  const handleExportCSV = () => {
    try {
      const headers = ['Date', 'Qui', 'Action', 'Quoi', 'Details'];
      const csvContent = [headers.join(','), ...logs.map((log) => [
        new Date(log.timestamp).toLocaleString('fr-FR'),
        log.user_name || log.user_email || '-',
        actionConfig[log.action]?.label || log.action,
        entityLabels[log.entity_type] || log.entity_type || '-',
        `"${(log.details || '-').replace(/"/g, '""')}"`,
      ].join(','))].join('\n');
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `journal_activite_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Export reussi');
    } catch { toast.error('Erreur lors de l\'export'); }
  };

  // Group repeated actions
  const groupedLogs = useMemo(() => groupRepeatedActions(logs), [logs]);
  const grouped = useMemo(() => groupByDay(groupedLogs), [groupedLogs]);
  const narrative = useMemo(() => buildNarrative(summary), [summary]);

  const topAction = stats?.by_action?.[0];
  const topActionLabel = topAction ? (actionConfig[topAction._id]?.label || topAction._id) : '-';

  return (
    <div className="space-y-5" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Journal d'activite</h2>
          <p className="text-gray-500 text-sm mt-1">Qui a fait quoi et quand</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExportCSV} variant="outline" size="sm" disabled={logs.length === 0}>
            <Download className="h-4 w-4 mr-2" /> Exporter
          </Button>
          <Button onClick={fetchAll} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" /> Actualiser
          </Button>
        </div>
      </div>

      {/* Résumé narratif du jour */}
      {narrative && (
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="py-3 flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-emerald-800 font-medium">{narrative}</p>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="py-4 text-center">
            <div className="text-2xl font-bold">{stats.total_logs?.toLocaleString()}</div>
            <p className="text-xs text-gray-500 mt-1">Actions totales</p>
          </CardContent></Card>
          <Card><CardContent className="py-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{summary?.total_today || stats.recent_24h}</div>
            <p className="text-xs text-gray-500 mt-1">Aujourd'hui</p>
          </CardContent></Card>
          <Card><CardContent className="py-4 text-center">
            <div className="text-2xl font-bold">{stats.top_users?.length || 0}</div>
            <p className="text-xs text-gray-500 mt-1">Utilisateurs actifs</p>
          </CardContent></Card>
          <Card><CardContent className="py-4 text-center">
            <div className="text-lg font-semibold text-gray-700">{topActionLabel}</div>
            <p className="text-xs text-gray-500 mt-1">Plus frequente</p>
          </CardContent></Card>
        </div>
      )}

      {/* Quick filter pills */}
      <div className="flex flex-wrap items-center gap-2">
        {quickFilters.map((f) => {
          const Icon = f.icon;
          const isActive = activeQuickFilter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => handleQuickFilter(f.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${
                isActive ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
              data-testid={`quick-filter-${f.key || 'all'}`}
            >
              <Icon className="h-3.5 w-3.5" />
              {f.label}
            </button>
          );
        })}
        <button
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 ml-2"
        >
          <Filter className="h-3 w-3" />
          Plus de filtres
          {showAdvancedFilters ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>
      </div>

      {showAdvancedFilters && (
        <Card>
          <CardContent className="py-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Input placeholder="Type: chauffeur, vehicule..." value={filters.entityType} onChange={(e) => setFilters({ ...filters, entityType: e.target.value })} />
              <Input type="date" value={filters.startDate} onChange={(e) => setFilters({ ...filters, startDate: e.target.value })} />
              <Input type="date" value={filters.endDate} onChange={(e) => setFilters({ ...filters, endDate: e.target.value })} />
              <Button variant="ghost" size="sm" onClick={() => { setFilters({ action: '', entityType: '', startDate: '', endDate: '' }); setActiveQuickFilter(''); }}>
                Reinitialiser
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      <div className="space-y-6">
        {loading ? (
          <div className="text-center py-12 text-gray-500">
            <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
            Chargement...
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Clock className="h-10 w-10 mx-auto mb-3" />
            Aucune activite trouvee
          </div>
        ) : (
          Object.entries(grouped).map(([dayLabel, dayLogs]) => (
            <div key={dayLabel}>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{dayLabel}</h3>
              <div className="space-y-1.5">
                {dayLogs.map((log) => {
                  const cfg = actionConfig[log.action] || { label: log.action, icon: Eye, color: 'text-gray-600 bg-gray-50 border-gray-200' };
                  const ActionIcon = cfg.icon;
                  const isExpanded = expandedLog === log.id;
                  const avatarColor = getAvatarColor(log.user_email);
                  const initials = getInitials(log.user_name, log.user_email);

                  return (
                    <div
                      key={log.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border transition-all cursor-pointer hover:shadow-sm ${cfg.color}`}
                      onClick={() => setExpandedLog(isExpanded ? null : log.id)}
                      data-testid={`log-entry-${log.id}`}
                    >
                      {/* Avatar */}
                      <div className={`h-8 w-8 rounded-full ${avatarColor} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                        {initials}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <ActionIcon className="h-3.5 w-3.5 flex-shrink-0" />
                          <p className="text-sm font-medium">
                            {buildSentence(log)}
                            {log._count > 1 && (
                              <span className="ml-1 text-xs font-normal opacity-70">({log._count} fois)</span>
                            )}
                          </p>
                        </div>
                        <p className="text-xs text-current/50 mt-0.5">{formatTime(log.timestamp)}</p>

                        {isExpanded && (
                          <div className="mt-2 pt-2 border-t border-current/10 text-xs space-y-1 opacity-80">
                            {log.user_email && <p>Email : {log.user_email}</p>}
                            {log.user_role && <p>Role : {log.user_role}</p>}
                            {log.entity_id && <p>Reference : {log.entity_id}</p>}
                            {log.ip_address && <p>Adresse IP : {log.ip_address}</p>}
                            {log.details && <p>Details : {log.details}</p>}
                          </div>
                        )}
                      </div>
                      <ChevronRight className={`h-4 w-4 opacity-40 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {logs.length > 0 && (
        <div className="flex items-center justify-between">
          <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} variant="outline" size="sm">Precedent</Button>
          <span className="text-sm text-gray-500">Page {page}</span>
          <Button onClick={() => setPage((p) => p + 1)} disabled={logs.length < 50} variant="outline" size="sm">Suivant</Button>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
